/**
 * Assessment Evidence Dashboard (DRR-213)
 *
 * MnCHOICES drift detection visualization.
 * Shows per-field comparison between assessed values and observed care patterns.
 *
 * Two views:
 * 1. Facility Overview — residents with high-confidence drift alerts
 * 2. Resident Detail — per-field drift analysis with evidence
 *
 * Backend-driven: all data from GraphQL assessment evidence pipeline.
 * QAPI scope required: RequireScope("qapi:view")
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  SimpleGrid,
  Loader,
  Table,
  Button,
  Progress,
  Select,
  Paper,
  RingProgress,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconReportAnalytics,
  IconPlayerPause,
  IconPlayerPlay,
  IconDownload,
  IconArrowLeft,
  IconCalendar,
  IconActivity,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { AccumulationStatusGql } from '@/generated/graphql';
import {
  useDriftAlerts,
  useResidentDrift,
  useFieldMappings,
  useAssessmentActions,
  DRIFT_SEVERITY_COLORS,
  DRIFT_DIRECTION_LABELS,
} from '@/hooks/useAssessmentEvidence';

// ============================================================================
// Types
// ============================================================================

type DriftSeverity = 'NONE' | 'MINOR' | 'MAJOR';
type DriftDirection = 'INCREASED' | 'DECREASED' | 'STABLE';

// ============================================================================
// Helper Components
// ============================================================================

function SeverityBadge({ severity }: { severity: string }) {
  const color = DRIFT_SEVERITY_COLORS[severity as DriftSeverity] ?? 'gray';
  return <Badge color={color}>{severity}</Badge>;
}

function DirectionIcon({ direction }: { direction: string }) {
  switch (direction) {
    case 'INCREASED':
      return <IconTrendingUp size={16} color="red" />;
    case 'DECREASED':
      return <IconTrendingDown size={16} color="blue" />;
    default:
      return <IconMinus size={16} color="gray" />;
  }
}

function ConfidenceRing({ confidence }: { confidence: number }) {
  const color =
    confidence >= 0.8 ? 'green' : confidence >= 0.5 ? 'yellow' : 'gray';
  return (
    <RingProgress
      size={40}
      thickness={4}
      sections={[{ value: confidence * 100, color }]}
      label={
        <Text size="xs" ta="center">
          {Math.round(confidence * 100)}%
        </Text>
      }
    />
  );
}

// ============================================================================
// Facility Overview View
// ============================================================================

function FacilityOverview({
  tenantId,
  onSelectResident,
}: {
  tenantId: string;
  onSelectResident: (residentId: string) => void;
}) {
  const { alerts, loading, error } = useDriftAlerts(tenantId);
  const { mappings } = useFieldMappings();

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
        <Text>Loading drift alerts...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading drift data">
        {error.message}
      </Alert>
    );
  }

  if (alerts.length === 0) {
    return (
      <Alert color="green" icon={<IconActivity />}>
        No significant drift detected across residents. Care patterns align with assessments.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Group justify="apart" mb="md">
          <Title order={4}>Active Drift Alerts</Title>
          <Badge color="red" size="lg">
            {alerts.length} fields with high-confidence drift
          </Badge>
        </Group>
        <Text c="dimmed" size="sm" mb="md">
          Fields where observed care patterns consistently differ from MnCHOICES assessment.
          Click a row to see detailed resident breakdown.
        </Text>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Field</Table.Th>
              <Table.Th>Assessed</Table.Th>
              <Table.Th>Observed</Table.Th>
              <Table.Th>Severity</Table.Th>
              <Table.Th>Confidence</Table.Th>
              <Table.Th>Consistent Days</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {alerts.map((alert, idx) => (
              <Table.Tr
                key={`${alert.fieldId}-${idx}`}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>
                  <Text fw={500}>{alert.fieldLabel}</Text>
                </Table.Td>
                <Table.Td>{alert.assessedLabel}</Table.Td>
                <Table.Td>{alert.observedLabel}</Table.Td>
                <Table.Td>
                  <SeverityBadge severity={alert.driftSeverity} />
                </Table.Td>
                <Table.Td>
                  <ConfidenceRing confidence={alert.confidence} />
                </Table.Td>
                <Table.Td>
                  <Badge variant="outline">{alert.consistentDays} days</Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Card withBorder>
        <Title order={4} mb="md">
          MnCHOICES Field Coverage
        </Title>
        <Text c="dimmed" size="sm" mb="md">
          11 of 14 MnCHOICES fields are tracked automatically.
          Clinical judgment fields (E7, E8, E9) require manual attestation.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {mappings.map((mapping) => (
            <Paper key={mapping.fieldId} p="sm" withBorder>
              <Group justify="apart">
                <Text size="sm" fw={500}>
                  {mapping.label}
                </Text>
                <Badge size="sm" variant="light">
                  {mapping.section}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                Field #{mapping.fieldNumber}
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
      </Card>
    </Stack>
  );
}

// ============================================================================
// Resident Detail View
// ============================================================================

function ResidentDetail({
  tenantId,
  residentId,
  onBack,
}: {
  tenantId: string;
  residentId: string;
  onBack: () => void;
}) {
  const { summary, results, loading, error, refetch } = useResidentDrift(
    tenantId,
    residentId
  );
  const { generateReport, pauseAccumulator, resumeAccumulator, loading: actionLoading } =
    useAssessmentActions();

  const handleGenerateReport = async () => {
    const report = await generateReport(tenantId, residentId);
    if (report) {
      // TODO: Download or display report
      console.log('Report generated:', report);
    }
  };

  const handlePause = async () => {
    await pauseAccumulator(tenantId, residentId, 'Hospitalization');
    refetch();
  };

  const handleResume = async () => {
    await resumeAccumulator(tenantId, residentId, false);
    refetch();
  };

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
        <Text>Loading resident drift data...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading resident data">
        {error.message}
      </Alert>
    );
  }

  const majorDriftFields = results.filter((r) => r.driftSeverity === 'MAJOR');
  const minorDriftFields = results.filter((r) => r.driftSeverity === 'MINOR');

  return (
    <Stack gap="md">
      <Group justify="apart">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={onBack}
        >
          Back to Overview
        </Button>
        <Group>
          {summary?.status === AccumulationStatusGql.ACTIVE ? (
            <Button
              variant="light"
              color="yellow"
              leftSection={<IconPlayerPause size={16} />}
              onClick={handlePause}
              loading={actionLoading}
            >
              Pause Accumulator
            </Button>
          ) : (
            <Button
              variant="light"
              color="green"
              leftSection={<IconPlayerPlay size={16} />}
              onClick={handleResume}
              loading={actionLoading}
            >
              Resume Accumulator
            </Button>
          )}
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleGenerateReport}
            loading={actionLoading}
            disabled={majorDriftFields.length === 0}
          >
            Generate Evidence Report
          </Button>
        </Group>
      </Group>

      {summary && (
        <Card withBorder>
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Status
              </Text>
              <Badge color={summary.status === AccumulationStatusGql.ACTIVE ? 'green' : 'yellow'}>
                {summary.status}
              </Badge>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Fields Tracked
              </Text>
              <Text fw={500}>{summary.totalFields}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Fields with Drift
              </Text>
              <Text fw={500} c={summary.fieldsWithDrift > 0 ? 'red' : 'green'}>
                {summary.fieldsWithDrift}
              </Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Major Drift
              </Text>
              <Text fw={500} c={summary.majorDriftFields > 0 ? 'red' : 'green'}>
                {summary.majorDriftFields}
              </Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Avg Confidence
              </Text>
              <Progress value={summary.avgConfidence * 100} color="blue" />
              <Text size="xs">{Math.round(summary.avgConfidence * 100)}%</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Days Since Assessment
              </Text>
              <Text fw={500}>{summary.daysSinceAssessment}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Last Updated
              </Text>
              <Text size="xs">{summary.lastUpdated}</Text>
            </Stack>
          </SimpleGrid>
        </Card>
      )}

      {majorDriftFields.length > 0 && (
        <Alert
          color="red"
          title="Major Drift Detected"
          icon={<IconAlertTriangle />}
        >
          {majorDriftFields.length} field(s) show major drift (2+ levels).
          Consider requesting MnCHOICES reassessment.
        </Alert>
      )}

      <Card withBorder>
        <Title order={4} mb="md">
          Per-Field Drift Analysis
        </Title>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Field</Table.Th>
              <Table.Th>Section</Table.Th>
              <Table.Th>Assessed</Table.Th>
              <Table.Th>Observed</Table.Th>
              <Table.Th>Drift</Table.Th>
              <Table.Th>Direction</Table.Th>
              <Table.Th>Confidence</Table.Th>
              <Table.Th>Days</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {results.map((result) => (
              <Table.Tr key={result.fieldId}>
                <Table.Td>
                  <Text fw={500}>{result.fieldLabel}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light">
                    {result.section}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Tooltip label={`Value: ${result.assessedValue}`}>
                    <Text>{result.assessedLabel}</Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Tooltip label={`Value: ${result.observedValue}`}>
                    <Text>{result.observedLabel}</Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <SeverityBadge severity={result.driftSeverity} />
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <DirectionIcon direction={result.driftDirection} />
                    <Text size="sm">
                      {DRIFT_DIRECTION_LABELS[result.driftDirection as DriftDirection] ??
                        result.driftDirection}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <ConfidenceRing confidence={result.confidence} />
                </Table.Td>
                <Table.Td>
                  <Badge variant="outline">{result.consistentDays}</Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}

// ============================================================================
// Main Dashboard Component
// ============================================================================

export default function AssessmentEvidenceDashboard() {
  const tenantId = useTenantId();
  const [selectedResidentId, setSelectedResidentId] = useState<string | null>(null);

  // For demo, allow selecting a test resident
  const [testResidentId, setTestResidentId] = useState('');

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="apart">
          <div>
            <Title order={2}>
              <Group gap="xs">
                <ThemeIcon size="lg" color="blue" variant="light">
                  <IconReportAnalytics size={20} />
                </ThemeIcon>
                MnCHOICES Drift Detection
              </Group>
            </Title>
            <Text c="dimmed" mt={4}>
              DRR-213: Continuous assessment evidence pipeline
            </Text>
          </div>
          {!selectedResidentId && (
            <Group>
              <Select
                placeholder="Enter resident ID"
                data={[
                  { value: 'R001', label: 'Test Resident R001' },
                  { value: 'R002', label: 'Test Resident R002' },
                ]}
                value={testResidentId}
                onChange={(v) => setTestResidentId(v ?? '')}
                clearable
                searchable
                w={200}
              />
              <Button
                disabled={!testResidentId}
                onClick={() => setSelectedResidentId(testResidentId)}
              >
                View Resident
              </Button>
            </Group>
          )}
        </Group>

        {selectedResidentId ? (
          <ResidentDetail
            tenantId={tenantId ?? 'hillside-harmony'}
            residentId={selectedResidentId}
            onBack={() => setSelectedResidentId(null)}
          />
        ) : (
          <FacilityOverview
            tenantId={tenantId ?? 'hillside-harmony'}
            onSelectResident={setSelectedResidentId}
          />
        )}
      </Stack>
    </Container>
  );
}
